import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import PageLayout from '../../components/layouts/PageLayout';
import { authService } from '../../auth/authService';
import { canAccessPage, ROLES, hasAnyRole } from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import {
  exportAiDashboard,
  getAiDashboard,
  type AiDashboardData,
} from '../../services/aiDashboardAnalyticsService';
import { analyticsDaysAgoIso, analyticsTodayIso } from '../../utils/analyticsTimezone';
import KpiCardGrid, { KpiCardItem } from '../../components/analytics/KpiCardGrid';
import AnalyticsDashboardSkeleton from '../../components/analytics/AnalyticsDashboardSkeleton';
import { formatPercent } from '../../components/analytics/chartUtils';
import { AnalyticsDateInput } from '../../utils/analyticsDateUtils';
import AiProcessingTrendChart from '../../components/analytics/AiProcessingTrendChart';
import AiFacilityResultsTable from '../../components/analytics/AiFacilityResultsTable';
import '../../components/analytics/analytics-theme.css';

function defaultFromDate(): string {
  return analyticsDaysAgoIso(30);
}

function formatSeconds(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  if (value < 60) {
    return `${Math.round(value)}s`;
  }
  const mins = value / 60;
  return `${Math.round(mins * 10) / 10} min`;
}

function formatRate(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  return formatPercent(value);
}

/**
 * RISPACS-1037 — AI analytics dashboard (facility-wise).
 */
export default function AiDashboard() {
  const user = authService.getUser();
  const isSuperAdmin = hasAnyRole(user, [ROLES.SUPER_ADMIN]);

  const [from, setFrom] = useState(defaultFromDate);
  const [to, setTo] = useState(analyticsTodayIso);
  const [facilityId, setFacilityId] = useState('all');
  const [classification, setClassification] = useState('all');
  const [trend, setTrend] = useState<'daily' | 'monthly'>('daily');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [data, setData] = useState<AiDashboardData | null>(null);
  // Keep facility options across failed loads so the filter bar stays usable (M3)
  const [facilityOptions, setFacilityOptions] = useState<Array<{ id: number; name: string }>>([]);

  const loadSeqRef = useRef(0);

  const query = useMemo(
    () => ({
      from,
      to,
      facility_ids: facilityId === 'all' ? undefined : facilityId,
      classification: classification === 'all' ? undefined : classification,
      trend,
    }),
    [from, to, facilityId, classification, trend]
  );

  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError(null);
    const result = await getAiDashboard(query);
    // Ignore stale responses when filters changed mid-flight (H2)
    if (seq !== loadSeqRef.current) {
      return;
    }
    if (result.success && result.data) {
      setData(result.data);
      if (result.data.filterOptions?.facilities?.length) {
        setFacilityOptions(result.data.filterOptions.facilities);
      }
    } else {
      setError(result.error || 'Failed to load AI dashboard');
      setData(null);
    }
    setLoading(false);
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const facilities = facilityOptions;

  const kpiItems: KpiCardItem[] = useMemo(() => {
    const k = data?.kpis;
    return [
      {
        key: 'processed',
        label: 'Total AI Processed Studies',
        value: k?.total_processed_studies ?? 0,
      },
      {
        key: 'success',
        label: 'AI Success Rate',
        value: formatRate(k?.success_rate),
      },
      {
        key: 'avgTime',
        label: 'Average AI Processing Time',
        value: formatSeconds(k?.avg_processing_seconds),
      },
      {
        key: 'override',
        label: 'Radiologist Override Rate',
        value: formatRate(k?.disagreement_rate),
      },
    ];
  }, [data?.kpis]);

  const trendChart = useMemo(
    () =>
      (data?.trend || []).map(p => ({
        bucket: p.bucket,
        normal: p.normal ?? 0,
        abnormal: p.abnormal ?? 0,
        failed_processing: p.failed_processing ?? p.failed + p.timeout,
      })),
    [data?.trend]
  );

  const handleClear = () => {
    setFrom(defaultFromDate());
    setTo(analyticsTodayIso());
    setFacilityId('all');
    setClassification('all');
    setTrend('daily');
    setExportError(null);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await exportAiDashboard(query, 'csv');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed';
      setExportError(message);
    } finally {
      setExporting(false);
    }
  };

  if (!canAccessPage(user, '/analytics/ai-dashboard')) {
    return (
      <Navigate
        to={getDefaultRoute(user)}
        replace
      />
    );
  }

  return (
    <PageLayout
      title="AI Dashboard"
      hideTitleInHeader
      breadcrumbs={[
        { label: 'Analytics', path: '/analytics' },
        { label: 'RIS' },
        { label: 'AI Results' },
      ]}
    >
      <div className="analytics-page analytics-dashboard ai-dashboard-page">
        <div className="analytics-filter-bar analytics-filter-bar--operational">
          <AnalyticsDateInput
            label="From"
            value={from}
            max={to}
            onChange={setFrom}
          />
          <AnalyticsDateInput
            label="To"
            value={to}
            min={from}
            max={analyticsTodayIso()}
            onChange={setTo}
          />
          {isSuperAdmin || facilities.length > 1 ? (
            <div className="analytics-filter-field">
              <label htmlFor="ai-facility">Facility</label>
              <select
                id="ai-facility"
                value={facilityId}
                onChange={e => setFacilityId(e.target.value)}
              >
                <option value="all">All facilities</option>
                {facilities.map(f => (
                  <option
                    key={f.id}
                    value={String(f.id)}
                  >
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="analytics-filter-field">
            <label htmlFor="ai-class">AI result</label>
            <select
              id="ai-class"
              value={classification}
              onChange={e => setClassification(e.target.value)}
            >
              <option value="all">All</option>
              <option value="NORMAL">Normal</option>
              <option value="ABNORMAL">Abnormal</option>
            </select>
          </div>
          <div className="analytics-filter-actions">
            <button
              type="button"
              className="analytics-text-btn"
              onClick={handleClear}
            >
              Clear
            </button>
            <button
              type="button"
              className="analytics-text-btn"
              onClick={load}
            >
              Refresh
            </button>
          </div>
        </div>

        {loading ? <AnalyticsDashboardSkeleton layout="operational" /> : null}

        {!loading && error ? (
          <div
            className="analytics-error"
            role="alert"
          >
            {error}{' '}
            <button
              type="button"
              className="analytics-text-btn"
              onClick={load}
            >
              Retry
            </button>
          </div>
        ) : null}

        {exportError ? (
          <div
            className="analytics-error"
            role="alert"
          >
            Export failed: {exportError}{' '}
            <button
              type="button"
              className="analytics-text-btn"
              onClick={handleExportCsv}
            >
              Retry export
            </button>
          </div>
        ) : null}

        {!loading && !error && data?.empty ? (
          <div className="analytics-empty">
            No AI processing data for the selected filters. Try a wider date range or another
            facility.
          </div>
        ) : null}

        {!loading && !error && data && !data.empty ? (
          <>
            <KpiCardGrid
              items={kpiItems}
              layout="operational"
            />
            <AiProcessingTrendChart
              data={trendChart}
              period={trend}
              onPeriodChange={setTrend}
            />

            {data.by_facility.length > 0 ? (
              <AiFacilityResultsTable
                rows={data.by_facility}
                showFacilityColumn={(data.filters.facility_ids?.length ?? 0) > 1}
                exporting={exporting}
                onExport={handleExportCsv}
              />
            ) : null}

            <p className="analytics-footnote">
              Classification uses the latest completed AI run per study. Success / fail / timeout
              and processing time use all run attempts. Agreement / disagreement use reviewed runs
              only (Correct / Incorrect). No confidence scores.
              {data.generated_at
                ? ` · Refreshed ${new Date(data.generated_at).toLocaleString()}`
                : ''}
            </p>
          </>
        ) : null}
      </div>
    </PageLayout>
  );
}
